import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { TimeReportForStamping } from "model/Company";
import { useCallback } from "react";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { formatMinutesDuration } from "web-apps/company/utils/timeAndDate";
import styled from "styled-components";
import {
  formatTimeLabel,
  formatTimeRangeLabel,
} from "web-apps/company/utils/timeAndDate";
import type { StampResult } from "./clockStamp.logic";

type Props = {
  timeReport?: TimeReportForStamping;
  stampResult: StampResult | null;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  isAutoGeneratedGig: boolean;
  setIsAutoGeneratedGig: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitTimeReport: () => void;
  clearInputs: () => void;
  workedMinutes: number | null;
};

export const ClockStampCheckInOutModal: React.FC<Props> = ({
  timeReport,
  stampResult,
  setShowModal,
  setKeyShouldBeDisabled,
  showModal,
  isAutoGeneratedGig,
  setIsAutoGeneratedGig,
  handleSubmitTimeReport,
  clearInputs,
  workedMinutes,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const isCheckOut =
    timeReport?.checkInTime && !isAutoGeneratedGig && !timeReport?.checkOutTime;
  const userName = (timeReport?.firstName || "").trim();
  const greeting = isCheckOut
    ? t("StampClock.HaveANiceDay")
    : t("StampClock.Welcome");
  const greetingText = userName ? `${greeting} ${userName}!` : `${greeting}!`;

  const checkInTime = stampResult?.checkInTime ?? timeReport?.checkInTime;
  const checkOutTime = stampResult?.checkOutTime ?? timeReport?.checkOutTime;

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setKeyShouldBeDisabled(false);
    setIsAutoGeneratedGig(false);
    clearInputs();
  }, [
    setShowModal,
    setKeyShouldBeDisabled,
    setIsAutoGeneratedGig,
    clearInputs,
  ]);

  return (
    <Modal
      label=""
      size="compact"
      isOpen={showModal}
      onClose={handleCloseModal}
    >
      <ModalContent>
        <Text typography="h3" align="center" color={ColorV2.Blue1}>
          {greetingText}
        </Text>
        {isCheckOut ? (
          <>
            <Text typography="b1" align="center" color={ColorV2.Blue1}>
              {t("StampClock.YouAreCheckedOutAt", {
                time: formatTimeLabel(checkOutTime, locale),
              })}
            </Text>
            {isCheckOut && workedMinutes !== null && (
              <Text typography="b1" align="center" color={ColorV2.Blue1}>
                {t("StampClock.YouWorked", {
                  duration: formatMinutesDuration(workedMinutes),
                })}
              </Text>
            )}
            <Actions>
              <PrimaryButton
                label={t("CompanyTimeReport.IWantToTimeReport")}
                onClick={handleSubmitTimeReport}
                size="small"
              />
            </Actions>
          </>
        ) : (
          <>
            <Text typography="b1" align="center" color={ColorV2.Blue1}>
              {t("StampClock.YouAreCheckedInAt", {
                time: formatTimeLabel(checkInTime, locale),
              })}
            </Text>
            {timeReport && (
              <ShiftInfo>
                <Text typography="b2" align="center" color={ColorV2.Blue1}>
                  {t("StampClock.ShiftToday")}
                </Text>
                <Text typography="b2" align="center" color={ColorV2.Blue1}>
                  {formatTimeRangeLabel(
                    timeReport.startTime,
                    timeReport.endTime,
                    locale,
                  )}
                </Text>
              </ShiftInfo>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ShiftInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 14px;
`;
